import { useEffect, useRef } from "react";
import io, { Socket } from "socket.io-client";
import type { VisualIndicatorJobStatus } from "../../components/Navbar/Settings/types/settings-drawer-interface";

const SOCKET_BASE_URL = process.env.REACT_APP_SOCKET_BASE_URL || "http://localhost:8095";

export const VISUAL_INDICATOR_PROGRESS_EVENT = "visual_indicator_progress";
export const VISUAL_INDICATOR_COMPLETED_EVENT = "visual_indicator_completed";

interface UseVisualIndicatorStreamArgs {
  userId: string | number | null | undefined;
  accountId?: string | number | null;
  onEvent: (event: VisualIndicatorJobStatus) => void;
  // Room emits are not buffered: progress fired while the socket was down is lost, so the
  // consumer re-reads the status endpoint here.
  onReconnect?: () => void;
}

// The relay may forward the I4 data block as is, or inside its notification envelope.
const dataBlock = (payload: any): VisualIndicatorJobStatus | null => {
  if (payload?.requestId) return payload;
  if (payload?.data?.requestId) return payload.data;
  return null;
};

/**
 * Subscribes to the notification socket for a bulk visual indicator rename's progress
 * (`visual_indicator_progress`, after each chunk) and outcome (`visual_indicator_completed`,
 * once). Published by simb-orchestration-ms and relayed by simb-notification-ms into the
 * `user:<id>` room. The consumer matches events to its request by requestId.
 */
const useVisualIndicatorStream = ({ userId, accountId, onEvent, onReconnect }: UseVisualIndicatorStreamArgs) => {
  // Keep latest callbacks in refs so the socket effect does not reconnect on every render.
  const onEventRef = useRef(onEvent);
  const onReconnectRef = useRef(onReconnect);
  onEventRef.current = onEvent;
  onReconnectRef.current = onReconnect;

  useEffect(() => {
    if (!userId) return;

    const socket: Socket = io(`${SOCKET_BASE_URL}/?userid=${userId}&accountId=${accountId ?? ""}`);

    const forward = (payload: any) => {
      const data = dataBlock(payload);
      if (data) onEventRef.current(data);
    };
    socket.on(VISUAL_INDICATOR_PROGRESS_EVENT, forward);
    socket.on(VISUAL_INDICATOR_COMPLETED_EVENT, forward);

    socket.io.on("reconnect", () => {
      onReconnectRef.current?.();
    });

    return () => {
      socket.disconnect();
    };
  }, [userId, accountId]);
};

export default useVisualIndicatorStream;
